import { CustomForm } from '../types/CustomForm'
import { AnswerState } from './dynamicFormReducer'

/**
 * 🔄 prefillDynamicFormAnswers: Maps saved registration form data to DynamicForm answers state
 *
 * This function takes the saved form data from registrationDetails.form and converts it
 * into the flat AnswerState format expected by DynamicForm components.
 *
 * @param selectedForm - The CustomForm definition containing groups and items
 * @param registrationForm - The saved form data from registrationDetails.form
 * @returns AnswerState - Flat mapping of item IDs to their values for DynamicForm
 *
 * @example
 * ```typescript
 * const selectedForm = { groups: [{ name: "Information", items: [...] }] }
 * const registrationForm = {
 *   information: [
 *     { id: "HngidcHvIeCdt0MV", name: "Phone", value: "+1234567890" },
 *     { id: "pn2H4JIw19GRFrgv", name: "Email", value: "test@example.com" }
 *   ]
 * }
 * const prefilled = prefillDynamicFormAnswers(selectedForm, registrationForm)
 * // Returns: { "HngidcHvIeCdt0MV": "+1234567890", "pn2H4JIw19GRFrgv": "test@example.com" }
 * ```
 */
export const prefillDynamicFormAnswers = (
  selectedForm: CustomForm | null,
  registrationForm?: {
    [sectionKey: string]: Array<{
      id: string
      name: string
      value: string
    }>
  }
): AnswerState => {
  console.log('🔄 prefillDynamicFormAnswers: Starting prefill process', {
    hasSelectedForm: !!selectedForm,
    hasRegistrationForm: !!registrationForm,
    registrationFormSections: registrationForm
      ? Object.keys(registrationForm)
      : [],
    selectedFormGroups: selectedForm?.groups?.length || 0,
  })

  // Return empty state if no form definition or registration data
  if (!selectedForm || !registrationForm) {
    console.log(
      '🔄 prefillDynamicFormAnswers: No form definition or registration data available'
    )
    return {}
  }

  const answers: AnswerState = {}
  let totalFieldsProcessed = 0
  let totalFieldsMatched = 0

  // Create a map of all valid field IDs from the form definition for fast lookup
  const validFieldIds = new Set<string>()
  selectedForm.groups.forEach((group) => {
    group.items.forEach((item) => {
      validFieldIds.add(item.id)
    })
  })

  console.log(
    '🔄 prefillDynamicFormAnswers: Valid field IDs from form definition',
    {
      validFieldCount: validFieldIds.size,
      validFieldIds: Array.from(validFieldIds),
    }
  )

  // Process each section in the registration form data
  Object.entries(registrationForm).forEach(([sectionKey, sectionData]) => {
    console.log('🔄 prefillDynamicFormAnswers: Processing section', {
      sectionKey,
      itemCount: sectionData?.length || 0,
    })

    if (!Array.isArray(sectionData)) {
      console.warn(
        '🔄 prefillDynamicFormAnswers: Section data is not an array',
        {
          sectionKey,
          dataType: typeof sectionData,
        }
      )
      return
    }

    // Process each field in the section
    sectionData.forEach((fieldData) => {
      totalFieldsProcessed++

      const { id, name, value } = fieldData

      // Validate field data structure
      if (!id || value === undefined || value === null) {
        console.log(
          '🔄 prefillDynamicFormAnswers: Skipping invalid field data',
          {
            sectionKey,
            fieldId: id,
            fieldName: name,
            hasValue: value !== undefined && value !== null,
          }
        )
        return
      }

      // Only include fields that exist in the current form definition
      if (validFieldIds.has(id)) {
        answers[id] = value
        totalFieldsMatched++
        console.log('🔄 prefillDynamicFormAnswers: Matched field', {
          sectionKey,
          fieldId: id,
          fieldName: name,
          fieldValue: value,
          valueType: typeof value,
        })
      } else {
        console.log(
          '🔄 prefillDynamicFormAnswers: Field ID not found in form definition',
          {
            sectionKey,
            fieldId: id,
            fieldName: name,
            fieldValue: value,
          }
        )
      }
    })
  })

  console.log('🔄 prefillDynamicFormAnswers: Prefill process completed', {
    totalFieldsProcessed,
    totalFieldsMatched,
    answersCount: Object.keys(answers).length,
    unmatchedFields: totalFieldsProcessed - totalFieldsMatched,
    answers,
  })

  return answers
}
